import React, { useState, useEffect, useRef, useContext, useMemo } from "react";
import Navbar from "../components/Navbar";
import useAxios from "../utils/useAxios";
import {
  useToast,
  Box,
  Center,
  Skeleton,
  Spinner,
  Button,
  Flex,
  Text,
  useColorModeValue,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Input,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Tooltip,
  HStack,
  Badge,
  Icon,
  VStack,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Divider,
} from "@chakra-ui/react";
import { ArrowLeftIcon, ArrowRightIcon, ChevronDownIcon, ChevronRightIcon } from "@chakra-ui/icons";
import Map, {
  Source,
  GeolocateControl,
  FullscreenControl,
  NavigationControl,
  ScaleControl,
} from "react-map-gl";
import type { ViewState, MapRef } from "react-map-gl";
import { Link as RouterLink } from "react-router-dom";
import RenderDevices from "../components/map/RenderDevices";
import RenderPolygon from "../components/map/RenderPolygon";
import RenderGateways from "../components/map/RenderGateways";
import RenderInterpolatedSurface from "../components/map/RenderInterpolatedSurface";
import { Trans } from "@lingui/react/macro";
import { IData, IDevice } from "../types/deviceInterface";
import { IField, IFieldId } from "../types/fieldInterface";
import {
  getMoistureIndexThresholds,
  MoistureIndexLegend,
  MoistureIndexThresholds,
} from "./deviceMapColorUtils";
import { getColorFunction, moistureIndexColorFn } from "../utils/colorUtils/mapColorUtils";
import { getCropTypeIcon } from "../utils/cropTypeMap";
import authContext from "../context/AuthContext";
import { FaSatelliteDish } from "react-icons/fa";
import { FaSeedling } from "react-icons/fa";
import RenderSatelliteData, {
  IndexName,
  SatelliteEntry,
} from "../components/map/RenderSateliteData";
import {
  SATELLITE_PRODUCTS, PRODUCT_DESCRIPTIONS, PRODUCT_LABELS, defaultRange, isScore, observationDate,
} from "../utils/satelliteProducts";
import AcquisitionDatePicker from "../components/map/AcquisitionDatePicker";
import SatelliteMapCard from "../components/map/SatelliteMapCard";
import SatelliteTimeSeriesChart from "../components/map/SatelliteTimeSeriesChart";
import { seriesDisplayRange, useSatelliteTimeSeries } from "../utils/satelliteTimeSeries";
import {
  Checkbox,
  Switch,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  RangeSlider,
  RangeSliderTrack,
  RangeSliderFilledTrack,
  RangeSliderThumb,
} from "@chakra-ui/react";

type PlotSelection = keyof IData | "moisture_status";

const PLOT_LABELS: Partial<Record<PlotSelection, string>> = {
  soil_moisture: "Soil Moisture (%)",
  soil_moisture_2: "Soil Moisture 2 (%)",
  soil_moisture_3: "Soil Moisture 3 (%)",
  soil_conductivity: "Soil Conductivity (dS/m)",
  soil_conductivity_2: "Soil Conductivity 2 (dS/m)",
  soil_conductivity_3: "Soil Conductivity 3 (dS/m)",
  air_temperature: "Air Temperature (C)",
  soil_temperature: "Soil Temperature (C)",
  moisture_status: "Moisture Status",
  moisture_index_1: "Moisture Index 1",
  moisture_index_2: "Moisture Index 2",
  moisture_index_3: "Moisture Index 3",
};

const MapScaleLegend = ({
  plotKey,
  mode,
  moistureMin,
  moistureMax,
  conductivityMin,
  conductivityMax,
  temperatureMin,
  temperatureMax,
  onChangeClick,
  showChangeButton,
  plotSelection,
  fieldStrategy,
  onPlotSelectionChange,
  moistureStatusLimits,
  moistureIndexThresholds,
}: {
  plotKey: keyof IData;
  mode: "default" | "moisture_status" | "moisture_index";
  moistureMin: number;
  moistureMax: number;
  conductivityMin: number;
  conductivityMax: number;
  temperatureMin: number;
  temperatureMax: number;
  onChangeClick?: () => void;
  showChangeButton?: boolean;
  plotSelection: PlotSelection;
  fieldStrategy: string;
  onPlotSelectionChange: (selection: PlotSelection) => void;
  moistureStatusLimits?: {
    moisture_limit_critical?: number;
    moisture_limit_damage?: number;
    moisture_limit_deficit?: number;
    moisture_limit_field_capacity?: number;
  };
  moistureIndexThresholds?: MoistureIndexThresholds;
}) => {
  const selectPlot = (event: React.MouseEvent, selection: PlotSelection) => {
    event.preventDefault();
    event.stopPropagation();
    onPlotSelectionChange(selection);
  };

  const selectedPlotLabel = PLOT_LABELS[plotSelection] ?? "Plot";

  let title = "Soil Moisture (%)";
  let min = moistureMin;
  let max = moistureMax;
  let gradient = "linear-gradient(90deg, rgb(255,0,0) 0%, rgb(0,255,0) 50%, rgb(0,0,255) 100%)";

  if (
    plotKey === "soil_conductivity" ||
    plotKey === "soil_conductivity_2" ||
    plotKey === "soil_conductivity_3"
  ) {
    title = "Soil Conductivity (dS/m)";
    min = conductivityMin;
    max = conductivityMax;
    gradient = "linear-gradient(90deg, rgb(255,0,0) 0%, rgb(255,165,0) 50%, rgb(0,255,0) 100%)";
  }

  if (
    plotKey === "air_temperature" ||
    plotKey === "air_temperature_2" ||
    plotKey === "soil_temperature"
  ) {
    title = "Temperature (°C)";
    min = temperatureMin;
    max = temperatureMax;
    gradient = "linear-gradient(90deg, rgb(0,0,255) 0%, rgb(255,255,255) 50%, rgb(255,0,0) 100%)";
  }

  const statusReady =
    mode === "moisture_status" &&
    moistureStatusLimits?.moisture_limit_critical !== undefined &&
    moistureStatusLimits?.moisture_limit_damage !== undefined &&
    moistureStatusLimits?.moisture_limit_deficit !== undefined &&
    moistureStatusLimits?.moisture_limit_field_capacity !== undefined;

  return (
    <Box mt={1} px={1}>
      <Flex justify="space-between" align="center" mb={1}>
        <Menu>
          <MenuButton as={Button} size="sm" variant="ghost" px={0} rightIcon={<ChevronDownIcon />}>
            {selectedPlotLabel}
          </MenuButton>
          <MenuList>
            <MenuItem
              onClick={(event) => selectPlot(event, "soil_moisture")}
              fontWeight={plotSelection === "soil_moisture" ? "semibold" : "normal"}
            >
              <Trans>Soil Moisture</Trans>
            </MenuItem>
            <MenuItem
              onClick={(event) => selectPlot(event, "soil_moisture_2")}
              fontWeight={plotSelection === "soil_moisture_2" ? "semibold" : "normal"}
            >
              <Trans>Soil Moisture 2</Trans>
            </MenuItem>
            <MenuItem
              onClick={(event) => selectPlot(event, "soil_moisture_3")}
              fontWeight={plotSelection === "soil_moisture_3" ? "semibold" : "normal"}
            >
              <Trans>Soil Moisture 3</Trans>
            </MenuItem>
            <MenuItem
              onClick={(event) => selectPlot(event, "soil_conductivity")}
              fontWeight={plotSelection === "soil_conductivity" ? "semibold" : "normal"}
            >
              <Trans>Soil Conductivity</Trans>
            </MenuItem>
            <MenuItem
              onClick={(event) => selectPlot(event, "soil_conductivity_2")}
              fontWeight={plotSelection === "soil_conductivity_2" ? "semibold" : "normal"}
            >
              <Trans>Soil Conductivity 2</Trans>
            </MenuItem>
            <MenuItem
              onClick={(event) => selectPlot(event, "soil_conductivity_3")}
              fontWeight={plotSelection === "soil_conductivity_3" ? "semibold" : "normal"}
            >
              <Trans>Soil Conductivity 3</Trans>
            </MenuItem>
            <MenuItem
              onClick={(event) => selectPlot(event, "air_temperature")}
              fontWeight={plotSelection === "air_temperature" ? "semibold" : "normal"}
            >
              <Trans>Air Temperature</Trans>
            </MenuItem>
            <MenuItem
              onClick={(event) => selectPlot(event, "soil_temperature")}
              fontWeight={plotSelection === "soil_temperature" ? "semibold" : "normal"}
            >
              <Trans>Soil Temperature</Trans>
            </MenuItem>
            {fieldStrategy === "soil_physics" && (
              <MenuItem
                onClick={(event) => selectPlot(event, "moisture_status")}
                fontWeight={plotSelection === "moisture_status" ? "semibold" : "normal"}
              >
                <Trans>Moisture Status</Trans>
              </MenuItem>
            )}
            {(["moisture_index_1", "moisture_index_2", "moisture_index_3"] as const).map(
              (key, index) => (
                <MenuItem
                  key={key}
                  onClick={(event) => selectPlot(event, key)}
                  fontWeight={plotSelection === key ? "semibold" : "normal"}
                >
                  <Trans>Moisture Index {index + 1}</Trans>
                </MenuItem>
              ),
            )}
          </MenuList>
        </Menu>
        {showChangeButton && (
          <Button size="xs" variant="outline" bg="white" onClick={onChangeClick}>
            <Trans>Change</Trans>
          </Button>
        )}
      </Flex>

      {mode === "moisture_status" ? (
        statusReady ? (
          <>
            <Flex h="14px" borderRadius="sm" overflow="hidden">
              <Box flex="1" bg="black" />
              <Box flex="1" bg="red" />
              <Box flex="1" bg="rgb(255, 165, 0)" />
              <Box flex="1" bg="rgb(0, 255, 0)" />
              <Box flex="1" bg="blue" />
            </Flex>
            <Flex justify="space-between" mt={1} fontSize="xs">
              <Text>PWP</Text>
              <Text>Damage</Text>
              <Text>Deficit</Text>
              <Text>Good</Text>
              <Text>Runoff</Text>
            </Flex>
          </>
        ) : (
          <Text fontSize="xs" color="gray.500">
            <Trans>Moisture limits are not configured for this field.</Trans>
          </Text>
        )
      ) : mode === "moisture_index" ? (
        <MoistureIndexLegend thresholds={moistureIndexThresholds} />
      ) : (
        <>
          <Box h="14px" borderRadius="sm" background={gradient} />
          <Flex justify="space-between" mt={1} fontSize="xs">
            <Text>{min}</Text>
            <Text>{((min + max) / 2).toFixed(1)}</Text>
            <Text>{max}</Text>
          </Flex>
        </>
      )}
    </Box>
  );
};

interface IPlotSettings {
  moisture_plot_minimum?: number;
  moisture_plot_maximum?: number;
  temperature_plot_minimum?: number;
  temperature_plot_maximum?: number;
  soil_conductivity_plot_minimum?: number;
  soil_conductivity_plot_maximum?: number;
}

interface DeviceMapViewProps {
  embedded?: boolean;
  initialFieldId?: number;
  lockToField?: boolean;
  hideFieldSelector?: boolean;
  hideFieldLink?: boolean;
  hideFieldSummary?: boolean;
}

const DeviceMapView: React.FC<DeviceMapViewProps> = ({
  embedded = false,
  initialFieldId,
  lockToField = false,
  hideFieldSelector = false,
  hideFieldLink = false,
  hideFieldSummary = false,
}) => {
  const api = useAxios();
  const toast = useToast();
  const mapRef = useRef<MapRef>(null);

  const _long = 10;
  const _lat = 50;
  const _zoom = 4;
  const _bearing = 0;
  const _pitch = 0;
  const _padding = { top: 0, bottom: 0, left: 0, right: 0 };

  const defaultView: ViewState = {
    longitude: _long,
    latitude: _lat,
    zoom: _zoom,
    bearing: _bearing,
    pitch: _pitch,
    padding: _padding,
  };

  const [initialViewState, setInitialViewState] = useState<ViewState>(defaultView);

  const flag = useRef(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingNewTimestamp, setIsLoadingNewTimestamp] = useState(false);
  const [devices, setDevices] = useState<IDevice[]>([]);
  const [fields, setFields] = useState<IFieldId[]>([]);
  const [viewFlag, setViewFlag] = useState(false);
  const [timestamp, setTimestamp] = useState<Date | null>(null);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isThresholdOpen,
    onOpen: onThresholdOpen,
    onClose: onThresholdClose,
  } = useDisclosure();
  const [selectedFieldId, setSelectedFieldId] = useState<number | "">("");
  const [selectedFieldDetails, setSelectedFieldDetails] = useState<IField | null>(null);

  const [satEnabled, setSatEnabled] = useState(false);
  const [satIndex, setSatIndex] = useState<IndexName>("NDVI");
  const [satOpacity, setSatOpacity] = useState<number>(0.85);
  // null = follow the range derived from the field's loaded series.
  const [satRangeOverride, setSatRangeOverride] = useState<[number, number] | null>(null);
  const [satEntries, setSatEntries] = useState<SatelliteEntry[]>([]);
  const [satSelectedId, setSatSelectedId] = useState<number | null>(null);
  const [satApplyToken, setSatApplyToken] = useState(0);
  const [satLoading, setSatLoading] = useState(false);
  const [satListLoading, setSatListLoading] = useState(false);
  const satEntriesRequestRef = useRef(0);
  const [is3DEnabled, setIs3DEnabled] = useState(false);
  const [showSurfaceGradient, setShowSurfaceGradient] = useState(false);
  const [isMapReady, setIsMapReady] = useState(false);
  const [isSavingThresholds, setIsSavingThresholds] = useState(false);
  const bgColor = useColorModeValue("white", "gray.900");
  const { userSettings } = useContext(authContext);
  const [plotSettings, setPlotSettings] = useState<IPlotSettings>({});
  const [thresholdDraft, setThresholdDraft] = useState<{ min: string; max: string }>({
    min: "",
    max: "",
  });
  const [plotSelection, setPlotSelection] = useState<PlotSelection>("soil_moisture");
  const shouldShowFieldSelector = !lockToField && !hideFieldSelector;

  const basePlotKey: keyof IData =
    plotSelection === "moisture_status" ? "soil_moisture" : (plotSelection as keyof IData);
  const isMoistureIndexSelection =
    plotSelection === "moisture_index" ||
    plotSelection === "moisture_index_1" ||
    plotSelection === "moisture_index_2" ||
    plotSelection === "moisture_index_3";

  const fieldStrategy = selectedFieldDetails?.management_feedback_strategy || "none";

  const fieldSpecificMode: "none" | "moisture_status" | "moisture_index" =
    plotSelection === "moisture_status"
      ? "moisture_status"
      : isMoistureIndexSelection
        ? "moisture_index"
        : "none";

  const moistureStatusParams =
    selectedFieldDetails && fieldSpecificMode === "moisture_status"
      ? {
          moisture_limit_critical: selectedFieldDetails.moisture_limit_critical,
          moisture_limit_damage: selectedFieldDetails.moisture_limit_damage,
          moisture_limit_deficit: selectedFieldDetails.moisture_limit_deficit,
          moisture_limit_field_capacity: selectedFieldDetails.moisture_limit_field_capacity,
        }
      : undefined;

  const moistureIndexThresholds =
    fieldSpecificMode === "moisture_index" ? getMoistureIndexThresholds(selectedFieldDetails) : {};
  const moistureIndexThresholdsByFieldId = useMemo(
    () =>
      Object.fromEntries(
        fields.map((field) => [
          field.id,
          getMoistureIndexThresholds(field),
        ]),
      ) as Record<number, MoistureIndexThresholds>,
    [fields],
  );

  const selectedVisualization = useMemo(() => {
    if (fieldSpecificMode === "moisture_status") {
      return null;
    }

    if (fieldSpecificMode === "moisture_index") {
      const moistureIndexParams: Record<string, number> = {
        management: moistureIndexThresholds.management ?? 0.5,
      };

      if (moistureIndexThresholds.deficit !== undefined) {
        moistureIndexParams.deficit = moistureIndexThresholds.deficit;
      }
      if (moistureIndexThresholds.upperManagement !== undefined) {
        moistureIndexParams.upperManagement = moistureIndexThresholds.upperManagement;
      }

      return {
        plotkey: basePlotKey,
        colorFunction: moistureIndexColorFn,
        colorParams: moistureIndexParams,
      };
    }

    const colorFunction = getColorFunction(basePlotKey);
    const { minKey, maxKey } = getThresholdKeysForPlot(basePlotKey);
    const min = Number(plotSettings[minKey]);
    const max = Number(plotSettings[maxKey]);

    return {
      plotkey: basePlotKey,
      colorFunction,
      colorParams: {
        min: Number.isFinite(min) ? min : 0,
        max: Number.isFinite(max) ? max : 1,
      },
    };
  }, [fieldSpecificMode, moistureIndexThresholds, basePlotKey, plotSettings]);

  useEffect(() => {
    setPlotSettings({
      moisture_plot_minimum: Number(userSettings.moisture_plot_minimum),
      moisture_plot_maximum: Number(userSettings.moisture_plot_maximum),
      temperature_plot_minimum: Number(userSettings.temperature_plot_minimum),
      temperature_plot_maximum: Number(userSettings.temperature_plot_maximum),
      soil_conductivity_plot_minimum: Number(userSettings.soil_conductivity_plot_minimum),
      soil_conductivity_plot_maximum: Number(userSettings.soil_conductivity_plot_maximum),
    });
  }, [userSettings]);

  function getThresholdKeysForPlot(key: keyof IData) {
    if (
      key === "soil_conductivity" ||
      key === "soil_conductivity_2" ||
      key === "soil_conductivity_3"
    ) {
      return {
        minKey: "soil_conductivity_plot_minimum" as const,
        maxKey: "soil_conductivity_plot_maximum" as const,
        label: "Soil Conductivity",
      };
    }

    if (key === "air_temperature" || key === "air_temperature_2" || key === "soil_temperature") {
      return {
        minKey: "temperature_plot_minimum" as const,
        maxKey: "temperature_plot_maximum" as const,
        label: "Temperature",
      };
    }

    return {
      minKey: "moisture_plot_minimum" as const,
      maxKey: "moisture_plot_maximum" as const,
      label: "Moisture",
    };
  }

  const openThresholdEditor = () => {
    const { minKey, maxKey } = getThresholdKeysForPlot(basePlotKey);
    const currentMin = plotSettings[minKey];
    const currentMax = plotSettings[maxKey];
    setThresholdDraft({
      min: currentMin !== undefined && Number.isFinite(currentMin) ? String(currentMin) : "",
      max: currentMax !== undefined && Number.isFinite(currentMax) ? String(currentMax) : "",
    });
    onThresholdOpen();
  };

  const saveThresholds = async () => {
    const minValue = Number(thresholdDraft.min);
    const maxValue = Number(thresholdDraft.max);
    if (!Number.isFinite(minValue) || !Number.isFinite(maxValue)) {
      toast({
        title: "Invalid threshold values",
        description: "Please provide numeric values for min and max.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    if (minValue >= maxValue) {
      toast({
        title: "Invalid range",
        description: "Minimum must be lower than maximum.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    const { minKey, maxKey } = getThresholdKeysForPlot(basePlotKey);
    const updatedSettings: IPlotSettings = {
      ...plotSettings,
      [minKey]: minValue,
      [maxKey]: maxValue,
    };

    setIsSavingThresholds(true);
    try {
      await api.put("frontend/frontend-settings/", updatedSettings);
      setPlotSettings(updatedSettings);
      localStorage.setItem("userSettings", JSON.stringify({ ...userSettings, ...updatedSettings }));
      toast({
        title: "Settings saved",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      onThresholdClose();
    } catch (error: any) {
      toast({
        title: "Error saving settings",
        description: error?.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSavingThresholds(false);
    }
  };

  useEffect(() => {
    if (flag.current) return;
    flag.current = true;
    (async () => {
      try {
        const [devRes, fldRes] = await Promise.all([
          api.get("frontend/devices/current-data/"),
          api.get("agtech/fields/"),
        ]);
        setDevices(devRes.data);
        setFields(fldRes.data);
        if (fldRes.data.length > 0 && !viewFlag) {
          const first = fldRes.data[0];
          setInitialViewState({
            longitude: first.field_center_lon,
            latitude: first.field_center_lat,
            zoom: 15,
            bearing: 0,
            pitch: 0,
            padding: { top: 0, bottom: 0, left: 0, right: 0 },
          });
          setViewFlag(true);
        }
      } catch (err: any) {
        if (err.response?.status !== 401) {
          toast({
            title: "Error fetching data",
            status: "error",
            duration: 9000,
            isClosable: true,
          });
        }
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const goToField = (field: IFieldId) => {
    const map = mapRef.current?.getMap();
    if (!map) return;

    const allCoordinates = Object.values(field.area || {}).flatMap((area: any) =>
      (area?.geometry?.coordinates || []).flatMap((ring: number[][]) => ring),
    );

    const validCoordinates = allCoordinates.filter(
      (coordinate: number[]) =>
        Array.isArray(coordinate) &&
        coordinate.length >= 2 &&
        Number.isFinite(coordinate[0]) &&
        Number.isFinite(coordinate[1]),
    );

    if (validCoordinates.length > 0) {
      const longitudes = validCoordinates.map((coordinate: number[]) => coordinate[0]);
      const latitudes = validCoordinates.map((coordinate: number[]) => coordinate[1]);
      const minLon = Math.min(...longitudes);
      const maxLon = Math.max(...longitudes);
      const minLat = Math.min(...latitudes);
      const maxLat = Math.max(...latitudes);

      if (
        Number.isFinite(minLon) &&
        Number.isFinite(maxLon) &&
        Number.isFinite(minLat) &&
        Number.isFinite(maxLat)
      ) {
        map.fitBounds(
          [
            [minLon, minLat],
            [maxLon, maxLat],
          ],
          {
            padding: { top: 48, right: 48, bottom: 48, left: 48 },
            duration: 900,
            maxZoom: 17,
          },
        );
        return;
      }
    }

    if (field.field_center_lon && field.field_center_lat) {
      map.flyTo({
        center: [field.field_center_lon, field.field_center_lat],
        zoom: 15,
        speed: 3,
        curve: 1,
        easing: (t) => t,
      });
    }
  };

  // datetime-local helpers
  const formatDateTimeLocal = (date: Date) => {
    const tzOffset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - tzOffset).toISOString().slice(0, 16);
  };
  const handleTimestampChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTimestamp(e.target.value ? new Date(e.target.value) : null);
  };
  const shiftTimestamp = (days: number) => {
    setTimestamp((prev) => {
      const base = prev || new Date();
      const newDate = new Date(base);
      newDate.setDate(base.getDate() + days);
      return newDate;
    });
  };

  const syncSensorTimeToSatelliteEntry = (entry: SatelliteEntry) => {
    const satelliteTimestamp = new Date(observationDate(entry));
    if (Number.isNaN(satelliteTimestamp.getTime())) return;

    setTimestamp(satelliteTimestamp);
    toast({
      title: <Trans>Sensor data time set to match satellite data time.</Trans>,
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  const handleSatelliteEntryChange = (satelliteDataId: number) => {
    const selectedEntry = satEntries.find((entry) => entry.satellite_data_id === satelliteDataId);

    setSatSelectedId(satelliteDataId);
    if (selectedEntry) syncSensorTimeToSatelliteEntry(selectedEntry);
  };

  const toggleSatellite = (next: boolean) => {
    setSatEnabled(next);
    if (!next) return;
    const fid = Number(selectedFieldId);
    if (satEntries.length) {
      const selectedEntry =
        satEntries.find((entry) => entry.satellite_data_id === satSelectedId) ?? satEntries[0];
      syncSensorTimeToSatelliteEntry(selectedEntry);
    } else if (Number.isFinite(fid) && fid > 0) {
      loadSatelliteEntries(fid, true);
    }
  };

  const handleSatelliteIndexChange = (value: string) => {
    const product = SATELLITE_PRODUCTS.find((name) => name === value);
    if (!product) return;
    setSatIndex(product);
    setSatRangeOverride(null);
    const available = satEntries.filter((entry) => entry.download_urls[product]);
    if (!available.some((entry) => entry.satellite_data_id === satSelectedId) && available[0]) {
      handleSatelliteEntryChange(available[0].satellite_data_id);
    }
  };

  const satEntriesForIndex = satEntries.filter((entry) => entry.download_urls[satIndex]);
  const satFieldId = Number(selectedFieldId);
  const { series: satSeries, loading: satSeriesLoading } = useSatelliteTimeSeries(
    satEnabled && Number.isFinite(satFieldId) && satFieldId > 0 ? satFieldId : null,
    satEntries.length,
  );
  const satAutoRange = useMemo(
    () => seriesDisplayRange(satSeries[satIndex], satIndex),
    [satSeries, satIndex],
  );
  const satRange = satRangeOverride ?? satAutoRange;

  useEffect(() => {
    if (timestamp === null) return;
    setIsLoadingNewTimestamp(true);
    (async () => {
      try {
        const res = await api.get("frontend/devices/current-data/", {
          params: { timestamp: timestamp.toISOString() },
        });
        setDevices(res.data);
        toast({
          title: "Data updated",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } catch (err: any) {
        if (err.response?.status !== 401) {
          toast({
            title: "Error fetching data",
            status: "error",
            duration: 9000,
            isClosable: true,
          });
        }
      } finally {
        setIsLoadingNewTimestamp(false);
      }
    })();
  }, [timestamp]);

  const loadSatelliteEntries = async (fieldId: number, syncSensorTime = false) => {
    const requestToken = ++satEntriesRequestRef.current;
    const isStale = () => requestToken !== satEntriesRequestRef.current;
    setSatListLoading(true);
    try {
      const res = await api.get(`satellite-data/by-field/${fieldId}/`);
      if (isStale()) return;
      const data = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.results)
          ? res.data.results
          : [];
      const list: SatelliteEntry[] = [...data].sort(
        (a, b) => new Date(observationDate(b)).getTime() - new Date(observationDate(a)).getTime(),
      );
      const first = list.find((entry) => entry.download_urls[satIndex]) ?? list[0];
      setSatEntries(list);
      setSatSelectedId(first ? first.satellite_data_id : null);
      if (syncSensorTime && first) syncSensorTimeToSatelliteEntry(first);
    } catch (e: any) {
      if (isStale()) return;
      console.error("[sat] load failed:", e?.response ?? e);
      toast({
        title: "Failed to load satellite list",
        description: e?.response?.status ? `HTTP ${e.response.status}` : undefined,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setSatEntries([]);
      setSatSelectedId(null);
    } finally {
      if (!isStale()) setSatListLoading(false);
    }
  };

  const handleSelectField = (f: IFieldId) => {
    if (lockToField) {
      return;
    }
    setSelectedFieldId(Number(f.id));
    onClose();
  };

  const focusedField =
    selectedFieldId === ""
      ? undefined
      : fields.find((field) => field.id === Number(selectedFieldId));

  useEffect(() => {
    if (selectedFieldId === "") {
      setSelectedFieldDetails(null);
      return;
    }

    let isCancelled = false;

    const loadFieldDetails = async () => {
      try {
        const response = await api.get(`agtech/fields/${selectedFieldId}/`);
        if (!isCancelled) {
          setSelectedFieldDetails(response.data);
        }
      } catch {
        if (!isCancelled) {
          setSelectedFieldDetails(null);
        }
      }
    };

    loadFieldDetails();

    return () => {
      isCancelled = true;
    };
  }, [selectedFieldId]);

  useEffect(() => {
    if (plotSelection === "moisture_status" && fieldStrategy !== "soil_physics") {
      setPlotSelection("soil_moisture");
    }
  }, [plotSelection, fieldStrategy]);

  const devicesForMap = devices;

  const focusedFieldForSurface =
    selectedFieldDetails ??
    (typeof selectedFieldId === "number"
      ? fields.find((field) => Number(field.id) === Number(selectedFieldId))
      : undefined);

  const focusedSurfaceDevices =
    typeof selectedFieldId === "number"
      ? devicesForMap.filter((device) => Number(device.field_id) === Number(selectedFieldId))
      : [];

  const hasSurfaceSamples = useMemo(() => {
    if (!selectedVisualization) {
      return false;
    }

    const validSampleCount = focusedSurfaceDevices.reduce((count, device) => {
      const raw = device.data?.[selectedVisualization.plotkey];
      const value = raw === undefined || raw === null ? NaN : Number(raw);

      if (!Number.isFinite(value)) {
        return count;
      }
      if (
        value === 0 &&
        !String(selectedVisualization.plotkey).startsWith("moisture_index")
      ) {
        return count;
      }
      if (!Number.isFinite(device.longitude) || !Number.isFinite(device.latitude)) {
        return count;
      }

      return count + 1;
    }, 0);

    return validSampleCount >= 2;
  }, [focusedSurfaceDevices, selectedVisualization]);

  const shouldRenderSurface =
    showSurfaceGradient && !!focusedFieldForSurface && !!selectedVisualization && hasSurfaceSamples;

  const legendMode: "default" | "moisture_status" | "moisture_index" =
    fieldSpecificMode === "none" ? "default" : fieldSpecificMode;

  const interactiveLayerIds = lockToField
    ? []
    : fields.flatMap((field) => [`field-line-${field.id}`, `field-fill-${field.id}`]);

  const handleMapFieldClick = (event: any) => {
    if (lockToField) {
      return;
    }

    const clickedFeature = event.features?.find((feature: any) => {
      const layerId = feature?.layer?.id;
      return typeof layerId === "string" && layerId.startsWith("field-");
    });

    if (!clickedFeature?.layer?.id) {
      return;
    }

    const layerId = String(clickedFeature.layer.id);
    const parsedFieldId = Number(layerId.replace("field-line-", "").replace("field-fill-", ""));

    if (!Number.isFinite(parsedFieldId)) {
      return;
    }

    setSelectedFieldId(parsedFieldId);
  };

  useEffect(() => {
    if (!isMapReady || selectedFieldId === "") return;
    const selectedField = fields.find((field) => Number(field.id) === Number(selectedFieldId));
    if (selectedField) {
      goToField(selectedField);
    }
  }, [selectedFieldId, fields, isMapReady]);

  useEffect(() => {
    // Drop the previous field's layer right away instead of showing it until the new list arrives.
    setSatEntries([]);
    setSatSelectedId(null);
    setSatRangeOverride(null);
    if (selectedFieldId === "" || selectedFieldId == null) {
      satEntriesRequestRef.current += 1;
      return;
    }
    loadSatelliteEntries(Number(selectedFieldId));
  }, [selectedFieldId]);

  useEffect(() => {
    if (!satEntries.length) {
      if (satSelectedId !== null) setSatSelectedId(null);
      return;
    }
    if (satSelectedId == null || !satEntries.some((e) => e.satellite_data_id === satSelectedId)) {
      const first = satEntries.find((e) => e.download_urls[satIndex]) ?? satEntries[0];
      setSatSelectedId(first.satellite_data_id);
    }
  }, [satEntries, satSelectedId, satIndex]);

  useEffect(() => {
    if (!lockToField || !Number.isFinite(initialFieldId)) {
      return;
    }

    setSelectedFieldId(Number(initialFieldId));
  }, [lockToField, initialFieldId]);

  useEffect(() => {
    if (!fields.length) return;
    if (lockToField) {
      if (Number.isFinite(initialFieldId)) {
        setSelectedFieldId(Number(initialFieldId));
      }
      return;
    }
    if (selectedFieldId === "" || selectedFieldId == null) {
      setSelectedFieldId(Number(fields[0].id));
    }
  }, [fields, selectedFieldId, lockToField, initialFieldId]);

  const content = (
    <Box maxW={embedded ? "100%" : "1200px"} mx="auto">
      <Box height={{ base: 500, lg: 700 }} position="relative">
        {!isLoading && (shouldShowFieldSelector || focusedField) && (
          <VStack position="absolute" top={4} left={4} zIndex={1} align={"start"}>
            {shouldShowFieldSelector && (
              <Button
                onClick={onOpen}
                variant="solid"
                size="sm"
                justifyContent="flex-start"
                bgColor="white"
                color="black"
                leftIcon={<Icon as={FaSeedling} boxSize={5} />}
                rightIcon={<ChevronDownIcon />}
              >
                {focusedField?.field_name ?? <Trans>Select Field</Trans>}
              </Button>
            )}
            {focusedField && (
              <Button
                onClick={() => toggleSatellite(!satEnabled)}
                size="sm"
                variant="solid"
                bgColor={satEnabled ? "blue.500" : "white"}
                color={satEnabled ? "white" : "black"}
                _hover={{ bgColor: satEnabled ? "blue.600" : "gray.100" }}
                leftIcon={<Icon as={FaSatelliteDish} boxSize={4} />}
                aria-pressed={satEnabled}
              >
                <Trans>Satellite</Trans>
              </Button>
            )}
          </VStack>
        )}

        {!isLoading && satEnabled && satEntries.length > 0 && (
          <SatelliteMapCard
            indexName={satIndex}
            valueRange={satRange}
            entries={satEntriesForIndex}
            selectedId={satSelectedId}
            isLoading={satLoading}
            onSelect={handleSatelliteEntryChange}
            availableProducts={SATELLITE_PRODUCTS.filter((product) =>
              satEntries.some((entry) => entry.download_urls[product]))}
            onIndexChange={handleSatelliteIndexChange}
          />
        )}

        {isLoading ? (
          <Center h="100%">
            <Skeleton w="100%" h="100%" />
          </Center>
        ) : (
          <Map
            ref={mapRef}
            mapLib={import("mapbox-gl")}
            mapStyle="mapbox://styles/mapbox/satellite-v9"
            initialViewState={initialViewState}
            mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
            style={{ width: "100%", height: "100%" }}
            terrain={is3DEnabled ? { source: "mapbox-dem", exaggeration: 1.5 } : undefined}
            interactiveLayerIds={interactiveLayerIds}
            onClick={handleMapFieldClick}
            onLoad={() => setIsMapReady(true)}
          >
            <Source
              id="mapbox-dem"
              type="raster-dem"
              url="mapbox://mapbox.mapbox-terrain-dem-v1"
              tileSize={512}
              maxzoom={14}
            />

            {fields.map((f) => (
              <RenderPolygon
                key={f.id}
                field={f}
                isFocused={selectedFieldId !== "" && Number(selectedFieldId) === Number(f.id)}
              />
            ))}

            {shouldRenderSurface && focusedFieldForSurface && selectedVisualization && (
              <RenderInterpolatedSurface
                key={`interpolated-surface-${selectedFieldId}`}
                field={focusedFieldForSurface}
                devices={focusedSurfaceDevices}
                plotkey={selectedVisualization.plotkey}
                colorFunction={selectedVisualization.colorFunction}
                colorParams={selectedVisualization.colorParams}
              />
            )}
            <RenderGateways />

            <RenderDevices
              devices={devicesForMap}
              plotkey={basePlotKey}
              referenceTimestamp={timestamp}
              mapRef={mapRef}
              plotSettingsOverride={plotSettings}
              fieldSpecificMode={fieldSpecificMode}
              focusedFieldId={typeof selectedFieldId === "number" ? selectedFieldId : undefined}
              moistureStatusParams={moistureStatusParams}
              moistureIndexParams={moistureIndexThresholds}
              moistureIndexParamsByFieldId={moistureIndexThresholdsByFieldId}
              popupPlotKey={basePlotKey}
            />

            <RenderSatelliteData
              mapRef={mapRef}
              enabled={satEnabled}
              indexName={satIndex}
              valueRange={satRange}
              opacity={satOpacity}
              entries={satEntries}
              selectedId={satSelectedId}
              applyToken={satApplyToken}
              onLoadingChange={setSatLoading}
            />

            <GeolocateControl />
            <FullscreenControl />
            <NavigationControl />
            <ScaleControl />
          </Map>
        )}
      </Box>

      <MapScaleLegend
        plotKey={basePlotKey}
        mode={legendMode}
        moistureMin={Number(plotSettings.moisture_plot_minimum) || 0}
        moistureMax={Number(plotSettings.moisture_plot_maximum) || 100}
        conductivityMin={Number(plotSettings.soil_conductivity_plot_minimum) || 0}
        conductivityMax={Number(plotSettings.soil_conductivity_plot_maximum) || 5}
        temperatureMin={Number(plotSettings.temperature_plot_minimum) || -10}
        temperatureMax={Number(plotSettings.temperature_plot_maximum) || 40}
        onChangeClick={openThresholdEditor}
        showChangeButton={fieldSpecificMode === "none"}
        plotSelection={plotSelection}
        fieldStrategy={fieldStrategy}
        onPlotSelectionChange={setPlotSelection}
        moistureStatusLimits={moistureStatusParams}
        moistureIndexThresholds={moistureIndexThresholds}
      />

      <Box mt={2} px={1}>
        <Flex
          direction={{ base: "column", md: "row" }}
          align={{ base: "flex-start", md: "center" }}
          justify="space-between"
          gap={2}
        >
          {!hideFieldLink && focusedField && (
            <Link
              as={RouterLink}
              to={`/fields/view/${focusedField.id}`}
              color="blue.500"
              textDecoration="underline"
              fontWeight="semibold"
            >
              <Trans>View field</Trans> {focusedField.field_name}
            </Link>
          )}
        </Flex>

        <Flex mt={2} align="center" justify="space-between">
          <Text fontSize="sm" fontWeight="medium">
            <Trans>3D terrain</Trans>
          </Text>
          <Switch isChecked={is3DEnabled} onChange={(e) => setIs3DEnabled(e.target.checked)} />
        </Flex>

        <Divider my={2} />

        <Flex mt={2} mb={2} align="center" justify="space-between">
          <Text fontSize="sm" fontWeight="medium">
            <Trans>Surface gradient overlay</Trans>
          </Text>
          <Switch
            isChecked={showSurfaceGradient}
            onChange={(e) => setShowSurfaceGradient(e.target.checked)}
            isDisabled={fieldSpecificMode === "moisture_status"}
          />
        </Flex>
      </Box>

      <Accordion allowToggle>
        <AccordionItem>
          <h2>
            <AccordionButton>
              <Box as="span" flex="1" textAlign="left">
                <Trans>History</Trans>
              </Box>
              <AccordionIcon />
            </AccordionButton>
          </h2>
          <AccordionPanel pb={4}>
            <Flex align="center" gap={2}>
              <Button leftIcon={<ArrowLeftIcon />} onClick={() => shiftTimestamp(-1)}>
                <Trans>Prev Day</Trans>
              </Button>
              <Input
                type="datetime-local"
                value={timestamp ? formatDateTimeLocal(timestamp) : ""}
                onChange={handleTimestampChange}
                maxW="250px"
              />
              <Button rightIcon={<ArrowRightIcon />} onClick={() => shiftTimestamp(1)}>
                <Trans>Next Day</Trans>
              </Button>
            </Flex>
            {isLoadingNewTimestamp && (
              <Flex mt={2} align="center">
                <Spinner size="sm" mr={2} />
                <Text>
                  <Trans>Loading data</Trans>...
                </Text>
              </Flex>
            )}
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem>
          <h2>
            <AccordionButton>
              <Box as="span" flex="1" textAlign="left">
                <Trans>Satellite</Trans>
              </Box>
              <AccordionIcon />
            </AccordionButton>
          </h2>

          <AccordionPanel pb={4}>
            <VStack align="stretch" spacing={4}>
              <HStack justify="space-between">
                <Text fontWeight="bold">
                  <Trans>Show satellite data</Trans>
                </Text>
                <Switch isChecked={satEnabled} onChange={(e) => toggleSatellite(e.target.checked)} />
              </HStack>

              {satEnabled && satListLoading && !satEntries.length && (
                <HStack fontSize="sm" color="gray.500">
                  <Spinner size="sm" />
                  <Text><Trans>Loading satellite images…</Trans></Text>
                </HStack>
              )}

              {satEnabled && !satListLoading && !satEntries.length && (
                <Text fontSize="sm" color="gray.500">
                  {focusedField
                    ? <Trans>No satellite images are available for this field yet.</Trans>
                    : <Trans>Select a field to load satellite images.</Trans>}
                </Text>
              )}

              {satEnabled && satEntries.length > 0 && (<>
              <Box>
                <Text fontWeight="bold" mb={2}>
                  <Trans>Index</Trans>
                </Text>
                <Flex wrap="wrap" gap={2} role="radiogroup" aria-label="Satellite index">
                  {SATELLITE_PRODUCTS.map((product) => (
                    <Tooltip key={product} label={PRODUCT_DESCRIPTIONS[product]} hasArrow openDelay={300}>
                      <Button
                        size="sm"
                        role="radio"
                        aria-checked={satIndex === product}
                        colorScheme={satIndex === product ? "blue" : "gray"}
                        variant={satIndex === product ? "solid" : "outline"}
                        isDisabled={!satEntries.some((entry) => entry.download_urls[product])}
                        onClick={() => handleSatelliteIndexChange(product)}
                      >
                        {PRODUCT_LABELS[product]}
                      </Button>
                    </Tooltip>
                  ))}
                </Flex>
              </Box>

              <SatelliteTimeSeriesChart
                indexName={satIndex}
                points={satSeries[satIndex]}
                selectedId={satSelectedId}
                onSelect={handleSatelliteEntryChange}
                loading={satSeriesLoading}
              />

              <AcquisitionDatePicker
                label={<Trans>Acquisition date</Trans>}
                entries={satEntriesForIndex}
                selectedId={satSelectedId}
                onChange={handleSatelliteEntryChange}
              />

              <Box>
                <HStack justify="space-between" mb={1}>
                  <Text fontWeight="bold">
                    <Trans>Colour range</Trans>
                  </Text>
                  <Button size="xs" variant="ghost" onClick={() => setSatRangeOverride(null)}>
                    <Trans>Reset</Trans>
                  </Button>
                </HStack>
                <HStack>
                  <Text w="48px" textAlign="right">
                    {isScore(satIndex) ? Math.round(satRange[0]) : satRange[0].toFixed(2)}
                  </Text>
                  <RangeSlider
                    min={defaultRange(satIndex)[0]}
                    max={defaultRange(satIndex)[1]}
                    step={isScore(satIndex) ? 1 : 0.01}
                    value={satRange}
                    onChange={(vals) => setSatRangeOverride(vals as [number, number])}
                    minStepsBetweenThumbs={1}
                  >
                    <RangeSliderTrack>
                      <RangeSliderFilledTrack />
                    </RangeSliderTrack>
                    <RangeSliderThumb index={0} />
                    <RangeSliderThumb index={1} />
                  </RangeSlider>
                  <Text w="48px">{isScore(satIndex) ? Math.round(satRange[1]) : satRange[1].toFixed(2)}</Text>
                </HStack>
              </Box>

              <Box>
                <Text fontWeight="bold" mb={1}>
                  <Trans>Opacity</Trans>
                </Text>
                <HStack>
                  <Slider min={0} max={1} step={0.05} value={satOpacity} onChange={setSatOpacity}>
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                  <Text w="40px" textAlign="right">
                    {Math.round(satOpacity * 100)}%
                  </Text>
                </HStack>
              </Box>
              </>)}
            </VStack>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>

      {shouldShowFieldSelector && (
        <Modal isOpen={isOpen} onClose={onClose} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              <Trans>Select Field</Trans>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {fields.map((f) => {
                return (
                  <Box
                    as="button"
                    key={f.id}
                    role="listitem"
                    onClick={() => handleSelectField(f)}
                    borderRadius="md"
                    p={3}
                    textAlign="left"
                    bg={bgColor}
                    _hover={{ shadow: "md", transform: "translateY(-1px)" }}
                    _active={{ transform: "translateY(0px)" }}
                    _focus={{ boxShadow: "outline" }}
                    mr={"6px"}
                  >
                    <HStack justify="space-between" align="center" spacing={4}>
                      <HStack spacing={3} align="center">
                        <Text fontSize="md" noOfLines={1}>
                          {f.field_name}
                        </Text>
                        <Icon
                          as={getCropTypeIcon(f.crop_type, f.crop_type_details?.name) || FaSeedling}
                          boxSize={5}
                          aria-hidden
                        />
                      </HStack>
                    </HStack>
                  </Box>
                );
              })}
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>
                <Trans>Cancel</Trans>
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      <Modal isOpen={isThresholdOpen} onClose={onThresholdClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Trans>Change Color Scale Thresholds</Trans>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={3} fontSize="sm" color="gray.500">
              {getThresholdKeysForPlot(basePlotKey).label}
            </Text>
            <FormControl mb={3}>
              <FormLabel>
                <Trans>Minimum</Trans>
              </FormLabel>
              <Input
                type="number"
                value={thresholdDraft.min}
                onChange={(e) =>
                  setThresholdDraft((prev) => ({
                    ...prev,
                    min: e.target.value,
                  }))
                }
              />
            </FormControl>
            <FormControl>
              <FormLabel>
                <Trans>Maximum</Trans>
              </FormLabel>
              <Input
                type="number"
                value={thresholdDraft.max}
                onChange={(e) =>
                  setThresholdDraft((prev) => ({
                    ...prev,
                    max: e.target.value,
                  }))
                }
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onThresholdClose}>
              <Trans>Cancel</Trans>
            </Button>
            <Button colorScheme="teal" onClick={saveThresholds} isLoading={isSavingThresholds}>
              <Trans>Save</Trans>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );

  if (embedded) {
    return content;
  }

  return <Navbar>{content}</Navbar>;
};

export default DeviceMapView;
